import { useState } from 'react';
import { Pencil, Save, X, CheckCircle, XCircle } from 'lucide-react';
import { format } from 'date-fns';
import type { UserDetail } from '../UserDetailPage';

interface Props {
  user: UserDetail;
  isEditing: boolean;
  isSaving: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: (updates: Partial<UserDetail>) => Promise<void>;
  onHardDelete: () => void;
}

// Extracted outside the component to prevent remount on every render
function BoolToggle({ label, displayValue, formValue, field, isEditing, onToggle }: {
  label: string;
  displayValue: boolean;
  formValue: boolean;
  field: string;
  isEditing: boolean;
  onToggle: (field: string) => void;
}) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-slate-700/50 last:border-0">
      <span className="text-sm text-slate-300">{label}</span>
      {isEditing ? (
        <button
          onClick={() => onToggle(field)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            formValue ? 'bg-cyan-600' : 'bg-slate-600'
          }`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            formValue ? 'translate-x-6' : 'translate-x-1'
          }`} />
        </button>
      ) : (
        <span className={`inline-flex items-center gap-1.5 text-sm ${displayValue ? 'text-green-400' : 'text-slate-500'}`}>
          {displayValue ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
          {displayValue ? 'Yes' : 'No'}
        </span>
      )}
    </div>
  );
}

export function UserAccountTab({ user, isEditing, isSaving, onEdit, onCancel, onSave, onHardDelete }: Props) {
  const [form, setForm] = useState({
    roleCode: user.roleCode,
    active: user.active,
    emailVerified: user.emailVerified,
    mobileVerified: user.mobileVerified,
    ageVerified: user.ageVerified,
    onboardingComplete: user.onboardingComplete,
    visibility: user.visibility || 'squads',
  });
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleSave = async () => {
    setSaveError(null);
    try {
      await onSave(form);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
    }
  };

  const handleCancel = () => {
    setForm({
      roleCode: user.roleCode,
      active: user.active,
      emailVerified: user.emailVerified,
      mobileVerified: user.mobileVerified,
      ageVerified: user.ageVerified,
      onboardingComplete: user.onboardingComplete,
      visibility: user.visibility || 'squads',
    });
    setSaveError(null);
    onCancel();
  };

  const handleToggle = (field: string) => {
    setForm(prev => ({ ...prev, [field]: !prev[field as keyof typeof prev] }));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white">Account Settings</h2>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <button onClick={handleCancel} disabled={isSaving} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors disabled:opacity-50">
                <X className="h-4 w-4" /> Cancel
              </button>
              <button onClick={handleSave} disabled={isSaving} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg transition-colors disabled:opacity-50">
                <Save className="h-4 w-4" /> {isSaving ? 'Saving...' : 'Save'}
              </button>
            </>
          ) : (
            <>
              <button onClick={onEdit} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors">
                <Pencil className="h-4 w-4" /> Edit
              </button>
              <button onClick={onHardDelete} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-red-800 hover:bg-red-700 text-white rounded-lg transition-colors border border-red-700">
                Hard Delete
              </button>
            </>
          )}
        </div>
      </div>

      {saveError && (
        <div className="px-4 py-2 bg-red-900/20 border border-red-800/50 rounded-lg text-sm text-red-400">{saveError}</div>
      )}

      {/* Role */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-6 space-y-4">
        <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider">Role & Permissions</h3>
        <div>
          <label className="block text-sm font-medium text-slate-400 mb-1">Role</label>
          {isEditing ? (
            <select
              value={form.roleCode}
              onChange={e => setForm(prev => ({ ...prev, roleCode: e.target.value }))}
              className="w-full max-w-xs px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-500"
            >
              <option value="TARK-USER">User</option>
              <option value="TARK-ADMIN">Admin</option>
              <option value="TARK-MODERATOR">Moderator</option>
            </select>
          ) : (
            <span className="text-sm text-slate-200">{(user.roleCode || 'TARK-USER').replace('TARK-', '')}</span>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-400 mb-1">Visibility</label>
          {isEditing ? (
            <select
              value={form.visibility}
              onChange={e => setForm(prev => ({ ...prev, visibility: e.target.value }))}
              className="w-full max-w-xs px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-500"
            >
              <option value="squads">Squads Only</option>
              <option value="public">Public</option>
              <option value="private">Private</option>
            </select>
          ) : (
            <span className="text-sm text-slate-200 capitalize">{user.visibility || 'squads'}</span>
          )}
        </div>
      </div>

      {/* Verification Flags */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-6 space-y-1">
        <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-3">Verification Flags</h3>
        <BoolToggle label="Account Active" displayValue={user.active} formValue={form.active} field="active" isEditing={isEditing} onToggle={handleToggle} />
        <BoolToggle label="Email Verified" displayValue={user.emailVerified} formValue={form.emailVerified} field="emailVerified" isEditing={isEditing} onToggle={handleToggle} />
        <BoolToggle label="Mobile Verified" displayValue={user.mobileVerified} formValue={form.mobileVerified} field="mobileVerified" isEditing={isEditing} onToggle={handleToggle} />
        <BoolToggle label="Age Verified" displayValue={user.ageVerified} formValue={form.ageVerified} field="ageVerified" isEditing={isEditing} onToggle={handleToggle} />
        <BoolToggle label="Onboarding Complete" displayValue={user.onboardingComplete} formValue={form.onboardingComplete} field="onboardingComplete" isEditing={isEditing} onToggle={handleToggle} />
      </div>

      {/* Timestamps */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-6 space-y-3">
        <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider">Timestamps</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-slate-400">Created:</span>{' '}
            <span className="text-slate-200">{user.createdAt ? format(new Date(user.createdAt), 'MMM d, yyyy h:mm a') : '\u2014'}</span>
          </div>
          <div>
            <span className="text-slate-400">Last Login:</span>{' '}
            <span className="text-slate-200">{user.lastLoginAt ? format(new Date(user.lastLoginAt), 'MMM d, yyyy h:mm a') : '\u2014'}</span>
          </div>
          <div>
            <span className="text-slate-400">Cognito Sub:</span>{' '}
            <span className="text-slate-200 font-mono text-xs">{user.cognitoSub || '\u2014'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
